import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  Observable,
  Subject,
  debounceTime,
  distinctUntilChanged,
  of,
  switchMap,
  takeUntil,
  map,
  catchError,
} from 'rxjs';
import { NavigationStart, Router } from '@angular/router';
import { Category } from '../../../core/models/category.model';
import { Product } from '../../../core/models/product.model';
import { AuthService } from '../../../core/services/auth.service';
import { CartService } from '../../../core/services/cart.service';
import { CompareService } from '../../../core/services/compare.service';
import { CategoriesService } from '../../../core/services/categories.service';
import { I18nService } from '../../../core/services/i18n.service';
import { ProductsService } from '../../../core/services/products.service';

export interface CategoriesSplit {
  topLevel: Category[];
  tree: Category[];
  all: Category[];
}

@Component({
  selector: 'app-site-header',
  templateUrl: './site-header.component.html',
  styleUrls: ['./site-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiteHeaderComponent implements OnInit, OnDestroy {
  /** Matches API / domain: Platform.Store — header nav is always ZeroTech Stores categories. */
  private static readonly headerCategoryPlatform = 0;
  private static readonly liveSearchMinChars = 2;
  private static readonly liveSearchLimit = 6;

  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;

  readonly categoriesSplit$: Observable<CategoriesSplit>;
  readonly isLoggedIn$ = this.authService.authStatus$;
  readonly cartCount$ = this.cartService.cartCount$;
  readonly compareCount$ = this.compareService.ids$.pipe(map((ids) => ids.length));
  readonly language$ = this.i18n.language$;
  categoriesDropdownOpen = false;
  mobileMenuOpen = false;
  mobileCategoriesOpen = false;
  /** Expanded parent category ids in the mobile drawer (chevron toggle). */
  private readonly expandedMobileCategoryIds = new Set<number>();
  activeRootCategoryId: number | null = null;
  hoveredCategoryId: number | null = null;
  subcategoryDropdownOpen = false;

  searchTerm = '';
  searchSuggestions: Product[] = [];
  searchOpen = false;
  searchLoading = false;
  searchActiveIndex = -1;
  private readonly searchInput$ = new Subject<string>();

  private openTimeout: any = null;
  private closeTimeout: any = null;
  private readonly OPEN_DELAY_MS = 120;
  private readonly CLOSE_DELAY_MS = 220;
  private readonly destroy$ = new Subject<void>();
  private previouslyFocusedElement: HTMLElement | null = null;

  constructor(
    private categoriesService: CategoriesService,
    private authService: AuthService,
    private cartService: CartService,
    private compareService: CompareService,
    private productsService: ProductsService,
    private i18n: I18nService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.categoriesSplit$ = this.categoriesService
      .getCategoryTree(undefined, SiteHeaderComponent.headerCategoryPlatform)
      .pipe(
      map((tree) => {
        const rawRoots = tree ?? [];
        // Only true roots (no parent). Orphans promoted when parent is missing from the tree stay out of the bar.
        const topLevel = this.filterNavTree(
          rawRoots.filter((c) => c.parentCategoryId == null || c.parentCategoryId === 0)
        );
        const all = this.flattenTree(topLevel);
        return {
          topLevel,
          tree: topLevel,
          all,
        };
      })
    );
    this.authService.authStatus$
      .pipe(
        switchMap(() => this.cartService.refreshCart()),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.cdr.markForCheck());

    this.cartService.cart$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cdr.markForCheck());

    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((evt) => {
        if (evt instanceof NavigationStart) {
          this.closeAllMenus();
          this.closeSearchSuggestions();
        }
      });

    this.searchInput$
      .pipe(
        debounceTime(250),
        map((term) => term.trim()),
        distinctUntilChanged(),
        switchMap((term) => {
          this.searchTerm = term;
          if (term.length < SiteHeaderComponent.liveSearchMinChars) {
            this.searchSuggestions = [];
            this.searchLoading = false;
            this.searchOpen = false;
            this.searchActiveIndex = -1;
            this.cdr.markForCheck();
            return of([] as Product[]);
          }
          this.searchLoading = true;
          this.searchOpen = true;
          this.cdr.markForCheck();
          const userId = this.authService.getUserId() ?? undefined;
          return this.productsService
            .searchProducts(term, 1, SiteHeaderComponent.liveSearchLimit, userId)
            .pipe(catchError(() => of([] as Product[])));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((products) => {
        this.searchSuggestions = products;
        this.searchLoading = false;
        this.searchOpen = this.searchTerm.length >= SiteHeaderComponent.liveSearchMinChars;
        this.searchActiveIndex = products.length ? 0 : -1;
        this.cdr.markForCheck();
      });
  }

  ngOnInit(): void {
    this.cartService.refreshCart().pipe(takeUntil(this.destroy$)).subscribe();
  }

  formatBadgeCount(count: number): string {
    if (!Number.isFinite(count) || count < 1) {
      return '';
    }
    return count > 99 ? '99+' : String(count);
  }

  toggleCategoriesDropdown(): void {
    this.categoriesDropdownOpen = !this.categoriesDropdownOpen;
    if (!this.categoriesDropdownOpen) {
      this.activeRootCategoryId = null;
    }
    this.cdr.markForCheck();
  }

  toggleMobileMenu(): void {
    if (this.mobileMenuOpen) {
      this.closeMobileMenu(true);
      return;
    }
    this.mobileMenuOpen = true;
    this.mobileCategoriesOpen = false;
    this.previouslyFocusedElement = this.document.activeElement as HTMLElement | null;
    this.lockBodyScroll();
    this.cdr.markForCheck();
    setTimeout(() => this.focusDrawerFirstItem());
  }

  closeMobileMenu(restoreFocus = false): void {
    if (!this.mobileMenuOpen) {
      return;
    }
    this.mobileMenuOpen = false;
    this.expandedMobileCategoryIds.clear();
    this.unlockBodyScroll();
    this.cdr.markForCheck();
    if (restoreFocus) {
      setTimeout(() => {
        const trigger = this.document.getElementById('mobileMenuButton') as HTMLElement | null;
        (trigger ?? this.previouslyFocusedElement)?.focus();
      });
    }
  }

  openCategoriesDropdown(split?: CategoriesSplit | null): void {
    this.categoriesDropdownOpen = !this.categoriesDropdownOpen;
    if (this.categoriesDropdownOpen) {
      this.closeSubcategoryDropdown();
    }
    if (!this.categoriesDropdownOpen) {
      this.activeRootCategoryId = null;
      this.cdr.markForCheck();
      return;
    }
    if (split?.tree?.length && !this.activeRootCategoryId) {
      this.activeRootCategoryId = split.tree[0].id;
    }
    this.cdr.markForCheck();
  }

  closeCategoriesDropdown(): void {
    this.categoriesDropdownOpen = false;
    this.activeRootCategoryId = null;
    this.cdr.markForCheck();
  }

  setActiveRoot(categoryId: number): void {
    this.activeRootCategoryId = categoryId;
    this.cdr.markForCheck();
  }

  getActiveRoot(split: CategoriesSplit | null | undefined): Category | null {
    const roots = split?.tree ?? [];
    if (!roots.length) {
      return null;
    }
    if (this.activeRootCategoryId == null) {
      return roots[0];
    }
    return roots.find((root) => root.id === this.activeRootCategoryId) ?? roots[0];
  }

  getChildren(category: Category): Category[] {
    return category.children ?? [];
  }

  hasChildren(category: Category): boolean {
    return this.getChildren(category).length > 0;
  }

  isMobileCategoryExpanded(categoryId: number): boolean {
    return this.expandedMobileCategoryIds.has(categoryId);
  }

  toggleMobileCategory(categoryId: number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.expandedMobileCategoryIds.has(categoryId)) {
      this.expandedMobileCategoryIds.delete(categoryId);
    } else {
      this.expandedMobileCategoryIds.add(categoryId);
    }
    this.cdr.markForCheck();
  }

  getLeafChildren(category: Category): Category[] {
    return this.getChildren(category).filter((child) => !this.hasChildren(child));
  }

  getBranchChildren(category: Category): Category[] {
    return this.getChildren(category).filter((child) => this.hasChildren(child));
  }

  logout(): void {
    this.authService.logout();
    this.cartService.clearLocalCart();
  }

  onSearch(term: string): void {
    const query = term.trim();
    this.closeSearchSuggestions();
    this.router.navigate(['/search'], { queryParams: query ? { q: query } : {} });
  }

  onSearchInput(term: string): void {
    this.searchInput$.next(term ?? '');
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (!this.searchOpen && event.key !== 'Escape') {
      if (event.key === 'Enter') {
        this.onSearch(this.searchInputRef?.nativeElement?.value ?? this.searchTerm);
      }
      return;
    }

    const max = this.searchSuggestions.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.searchActiveIndex = max < 0 ? -1 : Math.min(max, this.searchActiveIndex + 1);
        this.cdr.markForCheck();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.searchActiveIndex = max < 0 ? -1 : Math.max(0, this.searchActiveIndex - 1);
        this.cdr.markForCheck();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.searchActiveIndex >= 0 && this.searchSuggestions[this.searchActiveIndex]) {
          this.openSuggestion(this.searchSuggestions[this.searchActiveIndex]);
        } else {
          this.onSearch(this.searchInputRef?.nativeElement?.value ?? this.searchTerm);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeSearchSuggestions();
        break;
      default:
        break;
    }
  }

  openSuggestion(product: Product): void {
    this.closeSearchSuggestions();
    void this.router.navigate(['/product', product.id]);
  }

  viewAllSearchResults(): void {
    this.onSearch(this.searchTerm);
  }

  closeSearchSuggestions(): void {
    this.searchOpen = false;
    this.searchActiveIndex = -1;
    this.cdr.markForCheck();
  }

  trackByProductId = (_: number, product: Product): number => product.id;

  toggleLanguage(): void {
    this.i18n.toggleLanguage();
  }

  setLanguage(lang: 'ar' | 'en'): void {
    if (this.i18n.language === lang) {
      return;
    }
    this.i18n.setLanguage(lang);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearHoverTimers();
    this.unlockBodyScroll();
  }

  onCategoryHover(categoryId: number): void {
    this.clearHoverTimers();

    if (this.categoriesDropdownOpen) {
      this.categoriesDropdownOpen = false;
      this.activeRootCategoryId = null;
    }

    // While the panel is already open, switch instantly: the hover-intent delay
    // only guards the first open, so sliding along the bar feels continuous.
    if (this.subcategoryDropdownOpen) {
      this.showCategoryPanel(categoryId);
      return;
    }

    this.openTimeout = setTimeout(() => this.showCategoryPanel(categoryId), this.OPEN_DELAY_MS);
  }

  /** Keyboard parity: tabbing onto a category reveals the same panel as hover. */
  onCategoryFocus(categoryId: number): void {
    this.clearHoverTimers();
    this.showCategoryPanel(categoryId);
  }

  onCategoryLeave(): void {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
    this.scheduleClose();
  }

  onSubmenuHover(): void {
    this.clearHoverTimers();
  }

  onSubmenuLeave(): void {
    this.scheduleClose();
  }

  closeSubcategoryDropdown(): void {
    this.clearHoverTimers();
    this.subcategoryDropdownOpen = false;
    this.hoveredCategoryId = null;
    this.cdr.markForCheck();
  }

  closeAllMenus(): void {
    this.closeSubcategoryDropdown();
    this.categoriesDropdownOpen = false;
    this.closeMobileMenu();
    this.mobileCategoriesOpen = false;
    this.activeRootCategoryId = null;
    this.cdr.markForCheck();
  }

  toggleMobileCategories(): void {
    this.mobileCategoriesOpen = !this.mobileCategoriesOpen;
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeSearchSuggestions();
    this.closeAllMenus();
  }

  /** Outside click: close search suggestions and category hover panel. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const wrap = this.document.querySelector('.search-wrap');
    if (wrap && target && !wrap.contains(target)) {
      this.closeSearchSuggestions();
    }

    if (!this.subcategoryDropdownOpen) {
      return;
    }
    if (target?.closest('.category-nav')) {
      return;
    }
    this.closeSubcategoryDropdown();
  }

  private showCategoryPanel(categoryId: number): void {
    this.hoveredCategoryId = categoryId;
    this.subcategoryDropdownOpen = true;
    this.cdr.markForCheck();
  }

  private clearHoverTimers(): void {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  private scheduleClose(): void {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }
    this.closeTimeout = setTimeout(() => {
      this.subcategoryDropdownOpen = false;
      this.hoveredCategoryId = null;
      this.cdr.markForCheck();
    }, this.CLOSE_DELAY_MS);
  }

  private focusDrawerFirstItem(): void {
    const drawer = this.document.getElementById('mobileNavDrawer');
    const firstFocusable = drawer?.querySelector<HTMLElement>('a,button,[tabindex]:not([tabindex="-1"])');
    firstFocusable?.focus();
  }

  private lockBodyScroll(): void {
    this.document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    this.document.body.style.overflow = '';
  }

  getHoveredCategory(split: CategoriesSplit | null | undefined): Category | null {
    if (!this.hoveredCategoryId || !split?.all) return null;
    return split.all.find(c => c.id === this.hoveredCategoryId) || null;
  }

  trackByCategoryId(_: number, category: Category): number {
    return category.id;
  }

  categoryCount(category: Category): number | null {
    const total = category.totalProductCount ?? category.directProductCount;
    if (total != null && total > 0) return total;
    const childCount = category.children?.length ?? 0;
    return childCount > 0 ? childCount : null;
  }

  private filterNavTree(nodes: Category[]): Category[] {
    return [...(nodes ?? [])]
      .filter((node) => node.showOnNavigation !== false)
      .sort(
        (a, b) =>
          (a.navigationSortOrder ?? a.sortOrder ?? 0) - (b.navigationSortOrder ?? b.sortOrder ?? 0) ||
          a.id - b.id
      )
      .map((node) => ({
        ...node,
        children: this.filterNavTree(node.children ?? []),
      }));
  }

  private flattenTree(tree: Category[]): Category[] {
    const flat: Category[] = [];
    const walk = (nodes: Category[]) => {
      for (const node of nodes ?? []) {
        flat.push(node);
        if (node.children?.length) {
          walk(node.children);
        }
      }
    };
    walk(tree);
    return flat;
  }
}
